import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useChecklists, useChecklistSummary } from '../hooks';
import { Button } from '@/components/ui/button';
import { BulkAssignModal } from '../components/BulkAssignModal';

export function ChecklistListPage() {
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [assignOpen, setAssignOpen] = useState(false);
    const { data, isLoading } = useChecklists({
        status: statusFilter || undefined,
        page,
        per_page: 20,
    });
    const { data: summary } = useChecklistSummary();

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Checklists</h1>
                    <p className="text-sm text-muted-foreground">
                        Track assigned process checklists
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/checklists/templates">
                        <Button variant="outline">Templates</Button>
                    </Link>
                    <Button onClick={() => setAssignOpen(true)}>+ Assign Checklist</Button>
                </div>
            </div>

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-4 gap-4">
                    <div className="rounded-lg border bg-blue-50 p-3 text-blue-700">
                        <p className="text-sm">Active</p>
                        <p className="text-2xl font-bold">{summary.active}</p>
                    </div>
                    <div className="rounded-lg border bg-emerald-50 p-3 text-emerald-700">
                        <p className="text-sm">Completed</p>
                        <p className="text-2xl font-bold">{summary.completed}</p>
                    </div>
                    <div className="rounded-lg border bg-red-50 p-3 text-red-700">
                        <p className="text-sm">Overdue Steps</p>
                        <p className="text-2xl font-bold">{summary.overdue_steps}</p>
                    </div>
                    <div className="rounded-lg border bg-gray-50 p-3">
                        <p className="text-sm">Total</p>
                        <p className="text-2xl font-bold">{summary.total}</p>
                    </div>
                </div>
            )}

            {/* Filter */}
            <div className="flex gap-2">
                {['', 'active', 'completed', 'cancelled'].map((s) => (
                    <button
                        key={s}
                        className={`rounded-full px-3 py-1 text-sm ${statusFilter === s
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                            }`}
                        onClick={() => {
                            setStatusFilter(s);
                            setPage(1);
                        }}
                    >
                        {s || 'All'}
                    </button>
                ))}
            </div>

            {/* List */}
            {isLoading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : (
                <div className="space-y-3">
                    {data?.data?.map((c) => (
                        <Link
                            key={c.id}
                            to={`/checklists/${c.id}`}
                            className="flex items-center justify-between rounded-lg border p-4 transition hover:bg-muted/30"
                        >
                            <div>
                                <p className="font-medium">{c.template_name}</p>
                                <p className="text-xs text-muted-foreground">
                                    Started{' '}
                                    {new Date(c.start_date).toLocaleDateString('en-GB')}
                                </p>
                            </div>
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.status === 'active'
                                    ? 'bg-blue-50 text-blue-700'
                                    : c.status === 'completed'
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-gray-100 text-gray-500'
                                    }`}
                            >
                                {c.status}
                            </span>
                        </Link>
                    ))}
                    {data?.data?.length === 0 && (
                        <div className="rounded-lg border border-dashed p-8 text-center">
                            <p className="text-muted-foreground">
                                No checklists yet. Assign a template to get started.
                            </p>
                        </div>
                    )}
                </div>
            )}
            <BulkAssignModal open={assignOpen} onOpenChange={setAssignOpen} />
        </div>
    );
}